import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { slugify } from "@/lib/slug";
import {
  ArrowLeft, Trash2, Plus, Map, Pencil, Save, X, ExternalLink,
  Clapperboard, Palette, Search, Library, Landmark, Orbit, Brain, Compass,
  Globe, BookOpen, Mic, Camera, Hourglass, MessagesSquare,
} from "lucide-react";

const ICONS = {
  Clapperboard, Palette, Search, Library, Landmark, Orbit, Brain, Compass,
  Globe, BookOpen, Mic, Camera, Hourglass, MessagesSquare,
};
const ICON_KEYS = Object.keys(ICONS);
const MIN_HUB = 5;

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [intro, setIntro] = useState("");
  const [icon, setIcon] = useState("Compass");
  const [saving, setSaving] = useState(false);

  // inline edit
  const [editId, setEditId] = useState(null);
  const [eDesc, setEDesc] = useState("");
  const [eIntro, setEIntro] = useState("");
  const [eIcon, setEIcon] = useState("Compass");

  const load = () =>
    api.get("/categories").then((res) => setCats(res.data)).finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/posts").then((r) => setPosts(r.data || [])).catch(() => {});
  }, []);

  const counts = useMemo(() => {
    const m = {};
    posts.forEach((p) => {
      const c = (p.category || "").trim().toLowerCase();
      if (c) m[c] = (m[c] || 0) + 1;
    });
    return m;
  }, [posts]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await api.post("/categories", { name: name.trim(), description: description.trim(), intro: intro.trim(), icon });
      setCats((c) => [...c, res.data]);
      setName(""); setDescription(""); setIntro(""); setIcon("Compass");
      toast.success("Categoria creata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella creazione");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/categories/${id}`);
      setCats((c) => c.filter((x) => x.id !== id));
      toast.success("Categoria eliminata");
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const startEdit = (c) => {
    setEditId(c.id);
    setEDesc(c.description || "");
    setEIntro(c.intro || "");
    setEIcon(c.icon || "Compass");
  };

  const saveEdit = async (id) => {
    try {
      const res = await api.put(`/categories/${id}`, { description: eDesc.trim(), intro: eIntro.trim(), icon: eIcon });
      setCats((c) => c.map((x) => (x.id === id ? res.data : x)));
      setEditId(null);
      toast.success("Categoria aggiornata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="relative z-10 min-h-screen bg-[#F9F8F6]">
      <div className="border-b border-[#E7E5E4] bg-[#F2F0EB]">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 label-ui text-[#57534E] hover:text-[#9F583F] transition-colors">
            <ArrowLeft size={15} strokeWidth={1.5} /> Torna
          </Link>
          <Link to="/giraffe-map" target="_blank" className="inline-flex items-center gap-2 label-ui text-[#57534E] hover:text-[#9F583F] transition-colors">
            <Map size={15} strokeWidth={1.5} /> Vedi la mappa
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <p className="label-ui text-[#9F583F] mb-2">Giraffe Map · Pagine Hub</p>
        <h1 className="font-display text-4xl tracking-tight text-[#1C1917] mb-3">
          Categorie ({cats.length})
        </h1>
        <p className="font-body text-[#57534E] mb-10 max-w-2xl">
          Ogni categoria con almeno {MIN_HUB} articoli pubblicati diventa una <strong>pagina hub indicizzabile</strong> (/categoria/…). Scrivi un'introduzione originale per renderla forte agli occhi di Google.
        </p>

        {/* Create form */}
        <form onSubmit={create} className="border border-[#E7E5E4] bg-[#F2F0EB] p-6 mb-12" data-testid="category-form">
          <p className="font-display text-2xl text-[#1C1917] mb-5">Nuova categoria</p>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="label-ui text-[#57534E] block mb-2">Nome</label>
              <input
                data-testid="category-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Es. Psicologia"
                className="w-full font-ui text-sm border border-[#E7E5E4] bg-white px-3 py-2 focus:outline-none focus:border-[#9F583F]"
              />
            </div>
            <div>
              <label className="label-ui text-[#57534E] block mb-2">Icona</label>
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#9F583F]/12 text-[#9F583F] shrink-0">
                  {(() => { const I = ICONS[icon] || Compass; return <I size={20} strokeWidth={1.7} />; })()}
                </span>
                <select
                  data-testid="category-icon-select"
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                  className="w-full font-ui text-sm border border-[#E7E5E4] bg-white px-3 py-2 focus:outline-none focus:border-[#9F583F]"
                >
                  {ICON_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="mt-5">
            <label className="label-ui text-[#57534E] block mb-2">Descrizione breve (per la Giraffe Map)</label>
            <input
              data-testid="category-desc-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Breve descrizione della categoria"
              className="w-full font-ui text-sm border border-[#E7E5E4] bg-white px-3 py-2 focus:outline-none focus:border-[#9F583F]"
            />
          </div>
          <div className="mt-5">
            <label className="label-ui text-[#57534E] block mb-2">Introduzione della pagina hub (150-250 parole consigliate)</label>
            <textarea
              data-testid="category-intro-input"
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              rows={4}
              placeholder="Racconta di cosa parla questa raccolta: perché è interessante, cosa troverà il lettore, da dove iniziare…"
              className="w-full font-ui text-sm border border-[#E7E5E4] bg-white px-3 py-2 focus:outline-none focus:border-[#9F583F]"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="category-create-button"
            className="mt-6 inline-flex items-center gap-2 bg-[#1C1917] text-white label-ui px-5 py-3 hover:bg-[#9F583F] transition-colors disabled:opacity-60"
          >
            <Plus size={15} strokeWidth={1.5} /> {saving ? "Creazione…" : "Crea categoria"}
          </button>
        </form>

        {/* List */}
        {loading ? (
          <p className="font-ui text-sm text-[#57534E] py-10">Caricamento…</p>
        ) : cats.length === 0 ? (
          <div className="border border-dashed border-[#E7E5E4] py-16 text-center">
            <p className="font-display text-2xl italic text-[#57534E]">Nessuna categoria.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="categories-list">
            {cats.map((c) => {
              const I = ICONS[c.icon] || Compass;
              const n = counts[c.name.trim().toLowerCase()] || 0;
              const eligible = n >= MIN_HUB;
              const catSlug = slugify(c.name);
              const editing = editId === c.id;
              return (
                <div key={c.id} data-testid={`category-${c.name}`} className="border border-[#E7E5E4] bg-[#F2F0EB]">
                  <div className="p-5 flex items-center gap-4">
                    <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#9F583F]/12 text-[#9F583F] shrink-0">
                      <I size={22} strokeWidth={1.7} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display text-xl text-[#1C1917]">{c.name}</span>
                        {eligible ? (
                          <span className="label-ui text-white bg-[#6F7F35] px-2 py-0.5 rounded-full" data-testid={`hub-badge-${c.name}`}>Hub attivo · {n}</span>
                        ) : (
                          <span className="label-ui text-[#A8895F] bg-[#EADFC9] px-2 py-0.5 rounded-full">{n}/{MIN_HUB} per l'hub</span>
                        )}
                      </div>
                      <p className="font-body text-sm text-[#57534E] leading-snug">{c.description}</p>
                    </div>
                    {eligible && (
                      <Link to={`/categoria/${catSlug}`} target="_blank" title="Vedi la pagina hub" className="text-[#57534E] hover:text-[#9F583F] shrink-0"><ExternalLink size={16} /></Link>
                    )}
                    <button onClick={() => (editing ? setEditId(null) : startEdit(c))} data-testid={`edit-category-${c.name}`} className="text-[#A8A29E] hover:text-[#9F583F] shrink-0" title="Modifica"><Pencil size={16} /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`delete-category-${c.name}`} className="text-[#A8A29E] hover:text-red-600 transition-colors shrink-0" title="Elimina">
                          <Trash2 size={17} strokeWidth={1.5} />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-none bg-[#F9F8F6] border-[#E7E5E4]">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-display text-2xl">Eliminare "{c.name}"?</AlertDialogTitle>
                          <AlertDialogDescription className="font-body">
                            La categoria sparirà dalla Giraffe Map e dalla pagina hub. Gli articoli non verranno eliminati, ma resteranno senza questa categoria.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-none label-ui">Annulla</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(c.id)} data-testid="confirm-delete-category" className="rounded-none bg-red-700 hover:bg-red-800 label-ui">Elimina</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>

                  {editing && (
                    <div className="border-t border-[#E7E5E4] bg-white p-5 space-y-4" data-testid={`category-edit-${c.name}`}>
                      <div>
                        <label className="label-ui text-[#57534E] block mb-2">Icona</label>
                        <select value={eIcon} onChange={(e) => setEIcon(e.target.value)} className="w-full font-ui text-sm border border-[#E7E5E4] bg-[#F9F8F6] px-3 py-2 focus:outline-none focus:border-[#9F583F]">
                          {ICON_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="label-ui text-[#57534E] block mb-2">Descrizione breve</label>
                        <input value={eDesc} onChange={(e) => setEDesc(e.target.value)} className="w-full font-ui text-sm border border-[#E7E5E4] bg-[#F9F8F6] px-3 py-2 focus:outline-none focus:border-[#9F583F]" />
                      </div>
                      <div>
                        <label className="label-ui text-[#57534E] block mb-2">Introduzione hub</label>
                        <textarea data-testid={`category-intro-edit-${c.name}`} value={eIntro} onChange={(e) => setEIntro(e.target.value)} rows={5} className="w-full font-ui text-sm border border-[#E7E5E4] bg-[#F9F8F6] px-3 py-2 focus:outline-none focus:border-[#9F583F]" />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => saveEdit(c.id)} data-testid={`save-category-${c.name}`} className="inline-flex items-center gap-2 bg-[#9F583F] text-white label-ui px-4 py-2 hover:bg-[#1C1917] transition-colors"><Save size={14} /> Salva</button>
                        <button onClick={() => setEditId(null)} className="inline-flex items-center gap-2 label-ui text-[#57534E] px-4 py-2 hover:text-[#9F583F]"><X size={14} /> Annulla</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
